import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Heart, Trophy, Users } from "lucide-react";

type Rep = {
  id: string; fan_id: string; loyalty_score: number;
  support_total_cents: number; missions_completed: number; badges: string[];
};

export default function CreatorFans() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Rep[]>([]);
  const [names, setNames] = useState<Record<string, { display_name: string; cashtag: string }>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("fan_reputation")
        .select("*")
        .eq("creator_id", user.id)
        .order("loyalty_score", { ascending: false });
      const list = (data as any[] as Rep[]) || [];
      setRows(list);
      const ids = list.map((r) => r.fan_id);
      if (ids.length) {
        const { data: profs } = await supabase.from("profiles").select("user_id, display_name, cashtag").in("user_id", ids);
        const map: Record<string, any> = {};
        (profs as any[] | null)?.forEach((p) => { map[p.user_id] = p; });
        setNames(map);
      }
      setLoading(false);
    })();
  }, [user]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button asChild size="icon" variant="ghost"><Link to="/creator-mode"><ArrowLeft className="size-5" /></Link></Button>
          <h1 className="text-xl font-bold">Top Fans</h1>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-3">
        {loading ? <p className="text-center text-muted-foreground py-12">Loading…</p>
          : rows.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">
              <Users className="size-10 mx-auto mb-2" />
              No fan reputation data yet. Once supporters back you, they'll show up here.
            </CardContent></Card>
          ) : rows.map((r, i) => {
            const p = names[r.fan_id];
            return (
              <Card key={r.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-muted-foreground text-sm">#{i + 1}</span>
                      {p ? <Link to={`/u/${p.cashtag}`} className="hover:text-primary">{p.display_name}</Link> : "Fan"}
                    </CardTitle>
                    <Badge variant="secondary" className="flex items-center gap-1"><Trophy className="size-3" />{r.loyalty_score}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <p className="flex items-center gap-1"><Heart className="size-3.5 text-pink-500" />${(r.support_total_cents / 100).toFixed(2)} supported</p>
                  <p className="text-muted-foreground">{r.missions_completed} missions completed</p>
                  {Array.isArray(r.badges) && r.badges.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">{r.badges.map((b, j) => <Badge key={j} variant="outline" className="text-xs">🏅 {b}</Badge>)}</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
      </main>
    </div>
  );
}
